import { useIntl } from 'react-intl';

import { buildHoveredPointTooltipBodyProps } from './HoveredPointTooltip/hoveredPointTooltipBodyUtils.js';
import { buildHoveredPointTooltipHeaderProps } from './HoveredPointTooltip/hoveredPointTooltipHeaderUtils.js';
import { OverlayTooltip } from '../../../core/components/overlay-tooltip/OverlayTooltip.js';
import type { SeriesItemProps } from '../../../core/components/overlay-tooltip/types/series.js';
import { useLegendHidden } from '../../context/XYChartLegendHidden.context.js';
import { useAxesFormatter } from '../../hooks/useAxesFormatter.js';
import { useBarData } from '../../hooks/useBarData.js';
import { useFormatCategory } from '../../hooks/useFormatCategory.js';
import { useXYChartColorScales } from '../../hooks/useXYChartColorScales.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { useXYChartTruncationMode } from '../../hooks/useXYChartTruncationMode.js';
import type {
  BarSeriesToMetadata,
  BarSeriesWithDatapoints,
} from '../../types/xy-chart-internals.js';
import type { BarDatapoint } from '../../types/xy-chart.js';

export function TopListTooltip({
  datapoint,
  series,
  seriesToMetadata,
}: Readonly<{
  datapoint: BarDatapoint;
  series: BarSeriesWithDatapoints;
  seriesToMetadata: BarSeriesToMetadata;
}>) {
  const legendHidden = useLegendHidden();
  const { truncationMode } = useXYChartTruncationMode();
  const { axisIdToScaleDomain, datapointMetadataMap } = useXYChartData();
  const colorScaleMap = useXYChartColorScales();
  const axisIdToFormatter = useAxesFormatter();
  const formatCategory = useFormatCategory();
  const { filteredSeriesWithDatapoints } = useBarData();
  const intl = useIntl();

  const { name, color, actions } = buildHoveredPointTooltipHeaderProps({
    datapoint,
    series,
    seriesToMetadata,
    seriesWithDatapoints: filteredSeriesWithDatapoints,
    datapointMetadataMap,
    colorScaleMap,
  });

  const { xAxisValue, yAxisDuration } = buildHoveredPointTooltipBodyProps({
    datapoint,
    series,
    seriesToMetadata,
    axisIdToFormatter,
    axisIdToScaleDomain,
    datapointMetadataMap,
    formatCategory,
    intl,
  });

  const topListLabel = datapoint.label ?? '';
  const duration = yAxisDuration ? ` (${yAxisDuration})` : '';
  const valueText = `${xAxisValue}${duration}`;
  const data: SeriesItemProps[] = [
    { name: legendHidden ? topListLabel : name, value: valueText, color },
  ];

  return (
    <OverlayTooltip truncation={truncationMode}>
      {!legendHidden && <OverlayTooltip.Header header1={topListLabel} />}
      <OverlayTooltip.Series
        data={data}
        variant="shared"
        activeItem={-1}
        seriesItemTemplate={(item) => (
          <OverlayTooltip.SeriesItem {...item} actions={actions} />
        )}
      />
    </OverlayTooltip>
  );
}
